import { Text } from '@components/v2/ui';
import {
	Box,
	Flex,
	Group,
	Image,
	Popover,
	Stack,
} from '@mantine/core';
import {
	useDisclosure,
	useElementSize,
} from '@mantine/hooks';
import { IconChevronDown } from '@tabler/icons-react';
import { useCallback, useMemo } from 'react';
import classes from './LanguageSelect.module.css';

export interface LanguageOption {
	value: string;
	label: string;
	flagUrl: string;
}

export interface LanguageSelectProps {
	label?: string;
	value: string;
	options: LanguageOption[];
	onChange: (value: string) => void;
	width?: number | string;
	fullWidth?: boolean;
	withAsterisk?: boolean;
}

const computeWidth = (
	fullWidth?: boolean,
	width?: number | string
): string => {
	if (fullWidth) return '100%';
	if (width === undefined) return 'auto';
	return typeof width === 'number' ? `${width}px` : width;
};

export const LanguageSelect = ({
	label,
	value,
	options,
	onChange,
	width,
	fullWidth,
	withAsterisk,
}: LanguageSelectProps) => {
	const { ref, width: widthTarget } = useElementSize();

	const [opened, { toggle, close }] =
		useDisclosure(false);

	const selectedOption = useMemo(
		() =>
			options.find((o) => o.value === value) ??
			options[0],
		[options, value]
	);

	const handleSelect = useCallback(
		(v: string) => {
			onChange(v);
			close();
		},
		[onChange, close]
	);

	const resolvedWidth = useMemo(
		() => computeWidth(fullWidth, width),
		[fullWidth, width]
	);

	return (
		<Flex direction="column" flex={fullWidth ? 1 : 0}>
			{label && (
				<Group gap={4} mt={5} ml={24}>
					<Text
						c="#9BA6B7"
						variant="caption"
						style={{ display: 'inline-block' }}
					>
						{label}
					</Text>
					{withAsterisk && (
						<Text
							c="red"
							variant="caption"
							style={{ lineHeight: 1 }}
						>
							*
						</Text>
					)}
				</Group>
			)}
			<Popover
				opened={opened}
				onChange={(open) => (open ? toggle() : close())}
				withArrow
				shadow="md"
				radius="md"
				position="bottom"
				offset={0}
				zIndex={999}
			>
				<Popover.Target>
					<Box
						ref={ref}
						onClick={toggle}
						className={classes.targetLanguageSelect}
						style={{
							width: resolvedWidth,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							cursor: 'pointer',
						}}
					>
						<Group
							gap="xs"
							wrap="nowrap"
							style={{ flex: 1, minWidth: 0 }}
						>
							<Image
								className={classes.logoCountry}
								src={selectedOption.flagUrl}
								alt={selectedOption.label}
							/>
							<Text
								truncate
								style={{
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									flexShrink: 1,
								}}
							>
								{selectedOption.label}
							</Text>
						</Group>

						<Box
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								marginLeft: 8,
								flexShrink: 0,
							}}
						>
							<IconChevronDown size={18} stroke={1.8} />
						</Box>
					</Box>
				</Popover.Target>

				<Popover.Dropdown
					style={{
						width: widthTarget,
						padding: '15px 10px',
					}}
				>
					<Stack gap="10px">
						{options.map((option) => (
							<Box
								key={option.value}
								onClick={(e) => {
									e.stopPropagation();
									handleSelect(option.value);
								}}
								className={`${classes.customItem} ${
									option.value === value
										? classes.activeItem
										: ''
								}`}
							>
								<Image
									className={classes.logoCountry}
									src={option.flagUrl}
									alt={option.label}
								/>
								<Text>{option.label}</Text>
							</Box>
						))}
					</Stack>
				</Popover.Dropdown>
			</Popover>
		</Flex>
	);
};
